import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import _ from "lodash";
import moment from "moment";
import React, { useMemo, useState } from "react";

import { Charts } from "../../../constants/metrics";
import ChartWrapper from "./ChartWrapper";

// common responsive charts container used in side drawer and channel summary page

const ChartsContainer = (props) => {
  const { graphData, channelId, isSrt, inDrawer = false, timeStamp } = props;
  const [zoom, setZoom] = useState(false);
  const [selectedChart, setSelectedChart] = useState("");

  const antIcon = <LoadingOutlined style={{ fontSize: 30, align: "center" }} spin />;

  const mapTimeStamps = (time) => {
    const dateTime = new Date(time);
    const hours = dateTime.getHours();
    const minutes = dateTime.getMinutes();
    const day = dateTime.getDate();
    const month = dateTime.getMonth();
    const year = dateTime.getFullYear();

    const dateMonthString = `${day}-${month + 1}-${year.toString().slice(-2)}`;
    const modifiedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const modifiedHours = hours < 10 ? `0${hours}` : `${hours}`;

    if (minutes < 10) return `${dateMonthString} ${modifiedHours}:${modifiedMinutes}`;
    return `${dateMonthString} ${modifiedHours}:${modifiedMinutes}`;
  };

  const handleZoomChange = (selectedChart, zoomValue) => {
    setSelectedChart(selectedChart);
    setZoom(zoomValue);
  };

  const mapValues = (val) => val.toFixed(2);

  const findNearestIndex = (timeArray, targetTime) => {
    let minDifference = Number.MAX_SAFE_INTEGER;
    let closestIndex = -1;
    for (let i = 0; i < timeArray.length; i++) {
      if (Math.abs(moment(timeArray[i]).unix() - targetTime) < minDifference) {
        minDifference = Math.abs(moment(timeArray[i]).unix() - targetTime);
        closestIndex = i;
      }
    }
    return closestIndex;
  };
  const {
    networkIn,
    networkOut,
    inputVideoFrameRate,
    fillMsec,
    droppedFrames,
    activeAlerts,
    sourceBitRate,
    sourceTotalPackets,
  } = useMemo(() => {
    return {
      networkIn: {
        labels: graphData?.data?.NetworkIn?.Timestamps.map(mapTimeStamps),
        datasets: [
          {
            label: "Input Bitrate (Mbps)",
            data: graphData?.data?.NetworkIn?.Values.map(mapValues),
            borderColor: "blue",
            pointRadius: 0.001,
            pointHitRadius: 5,
            pointHoverRadius: 10,
            pointBackgroundColor: "blue",
            borderWidth: 2,
          },
        ],
      },
      networkOut: {
        labels: graphData?.data?.NetworkOut?.Timestamps.map(mapTimeStamps),
        datasets: [
          {
            label: "Output Bitrate (Mbps)",
            data: graphData?.data?.NetworkOut?.Values.map(mapValues),
            borderColor: "blue",
            pointRadius: 0.001,
            pointHitRadius: 5,
            pointHoverRadius: 10,
            pointBackgroundColor: "blue",
            borderWidth: 2,
          },
        ],
      },

      inputVideoFrameRate: {
        labels: graphData?.data?.InputVideoFrameRate?.Timestamps.map(mapTimeStamps),
        datasets: [
          {
            label: "Input Frame Rate",
            data: graphData?.data?.InputVideoFrameRate?.Values.map(mapValues),
            borderColor: "blue",
            pointRadius: 0.001,
            pointHitRadius: 5,
            pointHoverRadius: 10,
            pointBackgroundColor: "blue",
            borderWidth: 2,
          },
        ],
      },
      fillMsec: {
        labels: graphData?.data?.FillMsec?.Timestamps.map(mapTimeStamps),
        datasets: [
          {
            label: "Number of frames inserted when input is not present",
            data: graphData?.data?.FillMsec?.Values.map(mapValues),
            borderColor: "blue",
            pointRadius: 0.001,
            pointHitRadius: 5,
            pointHoverRadius: 10,
            pointBackgroundColor: "blue",
            borderWidth: 2,
          },
        ],
      },
      droppedFrames: {
        labels: graphData?.data?.DroppedFrames?.Timestamps.map(mapTimeStamps),
        datasets: [
          {
            label: "Number of frames dropped",
            data: graphData?.data?.DroppedFrames?.Values.map(mapValues),
            borderColor: "blue",
            pointRadius: 0.001,
            pointHitRadius: 5,
            pointHoverRadius: 10,
            pointBackgroundColor: "blue",
            borderWidth: 2,
          },
        ],
      },

      activeAlerts: {
        labels: graphData?.data?.ActiveAlerts?.Timestamps.map(mapTimeStamps),
        datasets: [
          {
            label: "Number of active alerts",
            data: graphData?.data?.ActiveAlerts?.Values.map(mapValues),
            borderColor: "blue",
            pointRadius: 0.001,
            pointHitRadius: 5,
            pointHoverRadius: 10,
            pointBackgroundColor: "blue",
            borderWidth: 2,
          },
        ],
      },
      sourceBitRate: {
        labels: graphData?.data?.SourceBitRate?.Timestamps.map(mapTimeStamps),
        datasets: [
          {
            label: "Bitrate of the incoming video",
            data: graphData?.data?.SourceBitRate?.Values.map(mapValues),
            borderColor: "blue",
            pointRadius: 0.001,
            pointHitRadius: 5,
            pointHoverRadius: 10,
            pointBackgroundColor: "blue",
            borderWidth: 2,
          },
        ],
      },
      sourceTotalPackets: {
        labels: graphData?.data?.SourceTotalPackets?.Timestamps.map(mapTimeStamps),
        datasets: [
          {
            label: "Total number of packets MediaConnect received",
            data: graphData?.data?.SourceTotalPackets?.Values.map(mapValues),
            borderColor: "blue",
            pointRadius: 0.001,
            pointHitRadius: 5,
            pointHoverRadius: 10,
            pointBackgroundColor: "blue",
            borderWidth: 2,
          },
        ],
      },
    };
  }, [graphData]);

  const targetIndex = useMemo(() => {
    return {
      networkIn: findNearestIndex(graphData?.data?.NetworkIn?.Timestamps ?? [], timeStamp),
      networkOut: findNearestIndex(graphData?.data?.NetworkOut?.Timestamps ?? [], timeStamp),
      inputVideoFrameRate: findNearestIndex(graphData?.data?.InputVideoFrameRate?.Timestamps ?? [], timeStamp),
      fillMsec: findNearestIndex(graphData?.data?.FillMsec?.Timestamps ?? [], timeStamp),
      droppedFrames: findNearestIndex(graphData?.data?.DroppedFrames?.Timestamps ?? [], timeStamp),
      activeAlerts: findNearestIndex(graphData?.data?.ActiveAlerts?.Timestamps ?? [], timeStamp),
      sourceBitRate: findNearestIndex(graphData?.data?.SourceBitRate?.Timestamps ?? [], timeStamp),
      sourceTotalPackets: findNearestIndex(graphData?.data?.SourceTotalPackets?.Timestamps ?? [], timeStamp),
    };
  }, [graphData]);
  return (
    <div className="container-fluid pt-3">
      {!channelId.length ? (
        <div className="text-center  pt-5 pb-5">
          <h3>No data found</h3>
        </div>
      ) : _.isEmpty(graphData) ? (
        <div className="bg-loader-fade">
          <Spin className="fade-loader" indicator={antIcon} />
          <div className="loader-back-text"></div>
        </div>
      ) : graphData?.success ? (
        zoom ? (
          <div className="cs-chart-block-zoomed">
            <ChartWrapper
              chartData={
                selectedChart === Charts.ACTIVE_ALERTS
                  ? activeAlerts
                  : selectedChart === Charts.NETWORK_IN
                    ? networkIn
                    : selectedChart === Charts.NETWORK_OUT
                      ? networkOut
                      : selectedChart === Charts.DROPPED_FRAMES
                        ? droppedFrames
                        : selectedChart === Charts.FILL_MILLISECS
                          ? fillMsec
                          : selectedChart === Charts.INPUT_VIDEO_FRAME_RATE
                            ? inputVideoFrameRate
                            : selectedChart === Charts.SOURCE_BITRATE
                              ? sourceBitRate
                              : selectedChart === Charts.SOURCE_TOTAL_PACKETS
                                ? sourceTotalPackets
                                : ""
              }
              chartLabel={
                selectedChart === Charts.ACTIVE_ALERTS
                  ? "Active Alerts"
                  : selectedChart === Charts.NETWORK_IN
                    ? "Network In(Mbps)"
                    : selectedChart === Charts.NETWORK_OUT
                      ? "Network Out(Mbps)"
                      : selectedChart === Charts.DROPPED_FRAMES
                        ? "Dropped Frames"
                        : selectedChart === Charts.FILL_MILLISECS
                          ? "Fill MilliSeconds"
                          : selectedChart === Charts.INPUT_VIDEO_FRAME_RATE
                            ? "Input Video Frame Rate"
                            : selectedChart === Charts.SOURCE_BITRATE
                              ? "Source BitRate"
                              : selectedChart === Charts.SOURCE_TOTAL_PACKETS
                                ? "Source Total Packets"
                                : ""
              }
              inDrawer={inDrawer}
              zoom={zoom}
              metricsName={selectedChart}
              setZoom={handleZoomChange}
              {...props}
            />
          </div>
        ) : (
          <div className="row">
            <div className="col-md-4 mb-3">
              <ChartWrapper
                chartData={networkIn}
                chartLabel="Network In(Mbps)"
                inDrawer={inDrawer}
                metricsName={Charts.NETWORK_IN}
                zoom={zoom}
                setZoom={handleZoomChange}
                targetIndex={targetIndex.networkIn}
                {...props}
              />
            </div>
            <div className="col-md-4 mb-3">
              <ChartWrapper
                chartData={networkOut}
                chartLabel="Network Out(Mbps)"
                inDrawer={inDrawer}
                metricsName={Charts.NETWORK_OUT}
                zoom={zoom}
                setZoom={handleZoomChange}
                targetIndex={targetIndex.networkOut}
                {...props}
              />
            </div>
            <div className="col-md-4 mb-3">
              <ChartWrapper
                chartData={inputVideoFrameRate}
                chartLabel="Input video frame rate"
                inDrawer={inDrawer}
                metricsName={Charts.INPUT_VIDEO_FRAME_RATE}
                zoom={zoom}
                setZoom={handleZoomChange}
                targetIndex={targetIndex.inputVideoFrameRate}
                {...props}
              />
            </div>
            <div className="col-md-4 mb-3">
              <ChartWrapper
                chartData={fillMsec}
                chartLabel="Fill milliseconds"
                inDrawer={inDrawer}
                metricsName={Charts.FILL_MILLISECS}
                zoom={zoom}
                setZoom={handleZoomChange}
                targetIndex={targetIndex.fillMsec}
                {...props}
              />
            </div>
            <div className="col-md-4 mb-3">
              <ChartWrapper
                chartData={droppedFrames}
                chartLabel="Dropped Frames"
                inDrawer={inDrawer}
                metricsName={Charts.DROPPED_FRAMES}
                zoom={zoom}
                setZoom={handleZoomChange}
                targetIndex={targetIndex.droppedFrames}
                {...props}
              />
            </div>
            <div className="col-md-4 mb-3">
              <ChartWrapper
                chartData={activeAlerts}
                chartLabel="Active Alerts"
                inDrawer={inDrawer}
                metricsName={Charts.ACTIVE_ALERTS}
                zoom={zoom}
                setZoom={handleZoomChange}
                targetIndex={targetIndex.activeAlerts}
                {...props}
              />
            </div>
            {isSrt && (
              <React.Fragment>
                <div className="col-md-4 mb-3">
                  <ChartWrapper
                    chartData={sourceBitRate}
                    chartLabel="Source Bitrate"
                    inDrawer={inDrawer}
                    metricsName={Charts.SOURCE_BITRATE}
                    zoom={zoom}
                    setZoom={handleZoomChange}
                    targetIndex={targetIndex.sourceBitRate}
                    {...props}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <ChartWrapper
                    chartData={sourceTotalPackets}
                    chartLabel="Source Total Packets"
                    inDrawer={inDrawer}
                    metricsName={Charts.SOURCE_TOTAL_PACKETS}
                    zoom={zoom}
                    setZoom={handleZoomChange}
                    targetIndex={targetIndex.sourceTotalPackets}
                    {...props}
                  />
                </div>
              </React.Fragment>
            )}
          </div>
        )
      ) : (
        <div className="text-center  pt-5 pb-5">
          <h3>No data found</h3>
        </div>
      )}
    </div>
  );
};
export default ChartsContainer;
